import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { ReactNode, useEffect } from 'react'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRoles?: string[]
}

// Admin portal requires Admin role for all routes
const ADMIN_PORTAL_REQUIRED_ROLE = 'Admin'
const MAIN_SITE_URL = 'https://hadv.quest'

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore()
  const location = useLocation()

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Save the location they were trying to access
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Always require Admin role for the admin portal
  const hasAdminRole = user?.roles?.includes(ADMIN_PORTAL_REQUIRED_ROLE)

  // Redirect non-admin users to main site
  useEffect(() => {
    if (isAuthenticated && !hasAdminRole) {
      console.warn('[ProtectedRoute] Non-admin user detected, redirecting to main site')
      window.location.href = MAIN_SITE_URL
    }
  }, [isAuthenticated, hasAdminRole])

  if (!hasAdminRole) {
    // Return null while redirecting
    return null
  }

  // Check additional role-based access if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => user?.roles?.includes(role))
    if (!hasRequiredRole) {
      console.warn('[ProtectedRoute] Access denied - missing required role')
      window.location.href = MAIN_SITE_URL
      return null
    }
  }

  return <>{children}</>
}
